import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { useClickAway } from 'react-use';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import { sendAiAssistantQuickAction } from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
// Adjust import paths as needed:
import { MAIN_QUICK_ACTIONS } from '../constants';
import { ALL, PLATFORM_LIST } from '../../../../../config/constants';
import { IMAGE } from '../../../config/constants';
import {
  PLATFORM_COLORS,
  PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL,
  PLATFORM_ICONS
} from '../../../../../config/icons';
import {InfoIconWithToolTip} from "../../include/InfoIconWithToolTip";
import {advancedAiTokensAvailable} from "../utils/tokens";
import LomavisUpgradeSubscriptionPageLink from "../../../../billing/utils/openSubscriptionPageLink";
import {useSelector} from "react-redux";

interface Props {
  t: (key: string) => string;
  dispatch: any;
  useSeparateMediaFiles: boolean;
  availableTokens: number;
  usedTokens: number; // if you also need to show used tokens, pass it in
  postData: any;
  advancedDataButton: boolean;
}

const AnalyzeImageAction: React.FC<Props> = ({
  t,
  dispatch,
  useSeparateMediaFiles,
  availableTokens,
  usedTokens,
  postData,
    advancedDataButton= false
}) => {
  const ANALYZE_IMAGE_ACTION = MAIN_QUICK_ACTIONS.find(
    (item) => item.action === 'analyzeImage'
  );

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});
  const dropdownTriggerRef = useRef<HTMLDivElement>(null);

  const {
    aiAssistant: {
      conversation: { messageSettings }
    }
  } = useSelector((state: any) => state.lomavisCreator);


  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const triggerElement = dropdownTriggerRef.current;
      if (triggerElement) {
        const rect = triggerElement.getBoundingClientRect();
        setDropdownStyles({
          position: 'absolute',
          top: rect.bottom + window.scrollY - dropdownRef.current.offsetHeight,
          left: rect.left + window.scrollX,
          zIndex: 9999
        });
      }
    }
  }, [showDropdown]);

  useClickAway(dropdownRef, () => {
    setShowDropdown(false);
  });

  const getPlatformsWithMediaImages = () => {
    return PLATFORM_LIST.filter((platform) => {
      const images = postData?.[platform]?.media?.filter((item: any) =>
        item.file_type?.includes(IMAGE)
      );
      return images && images.length > 0;
    });
  };

  const availablePlatforms = useSeparateMediaFiles
    ? getPlatformsWithMediaImages()
    : [];

  const handleAnalyzeImageClick = (platform?: string): void => {
    interface MediaItem {
      file_type?: string;
      uuid: string;
    }
    let media: string[] = [];
    if (!useSeparateMediaFiles) {
      media = postData?.[ALL]?.media
        ?.filter((item: MediaItem) => item.file_type?.includes(IMAGE))
        .map((item: MediaItem) => item.uuid);
    } else {
      media = postData?.[platform]?.media
        ?.filter((item: MediaItem) => item.file_type?.includes(IMAGE))
        .map((item: MediaItem) => item.uuid);
    }

    if (!media || media.length === 0) {
      toast.error(t('lomavis.branding.mainActions.noImages'), {
        duration: 2000,
        position: 'top-right'
      });
      return;
    }

    dispatch(
      sendAiAssistantQuickAction(ANALYZE_IMAGE_ACTION, {
        images: media,
        platform: platform ?? undefined,
        ...messageSettings
      }) as any
    );
  };

  // Dropdown markup
  const Dropdown = (
    <div
      style={dropdownStyles}
      ref={dropdownRef}
      className="dropdown-menu show p-4"
    >
      <h5 className="dropdown-header">
        {t('lomavis.branding.mainActions.selectPlatform')}
      </h5>
      <div className="dropdown-divider"></div>
      {availablePlatforms.map((platform) => (
        <div
          key={platform}
          className="d-flex align-items-center cursor-pointer dropdown-item px-1 py-2"
          onClick={() => {
            handleAnalyzeImageClick(platform);
            setShowDropdown(false);
          }}
        >
          <i
            className={`me-2 ${PLATFORM_ICONS[platform]} text-center w-25px`}
            style={{
              color: PLATFORM_COLORS[platform],
              fontSize: 20
            }}
          ></i>
          {t(PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL[platform])}
        </div>
      ))}
      {availablePlatforms.length === 0 && (
        <span className="dropdown-item disabled">
          {t('lomavis.branding.mainActions.noPlatforms')}
        </span>
      )}
    </div>
  );

  const card = (
    <div
      key={ANALYZE_IMAGE_ACTION?.id}
      className="btn btn-secondary  btn-sm cursor-pointer"
      style={{whiteSpace: "nowrap", backgroundImage: "linear-gradient(90deg, rgb(37, 117, 252), rgb(106, 17, 203))"}}
      onClick={() => {
        // if no available tokens, do nothing
        if (!advancedAiTokensAvailable(usedTokens, availableTokens)) {
          return;
        }
        if (useSeparateMediaFiles) {
          setShowDropdown((prev) => !prev);
        } else {
          handleAnalyzeImageClick();
        }
      }}
    >
      <div className="d-flex w-100 align-items-center justify-content-between text-white" ref={dropdownTriggerRef}>
        <div className="d-flex flex-row align-items-center">
          <div className="d-flex align-items-center text-white">
            <i className={`${ANALYZE_IMAGE_ACTION?.icon} me-2 w-20px text-white`} />
          </div>
          <div className="d-flex align-items-center text-white">
            {t(ANALYZE_IMAGE_ACTION?.name)}
          </div>
          <div className="d-flex align-items-center ps-2  text-white">
            <InfoIconWithToolTip text={t("lomavisCreator.ai.quickActionForImages.rewriteText")} placement={"top"} showTokens={true}
                                 availableTokens={availableTokens}
                                 usedTokens={usedTokens} advancedDataButton={true} />
          </div>
        </div>
      </div>

      {/* Portal for the dropdown */}
      {useSeparateMediaFiles &&
        showDropdown &&
        ReactDOM.createPortal(Dropdown, document.body)}
    </div>
  );

  return !advancedAiTokensAvailable(usedTokens, availableTokens) ? (
    <OverlayTrigger
        delay={{ show: 100, hide: 1000 }}
      placement="top"
      overlay={
        <Tooltip id="tooltip-no-tokens">
          <div className={"text-start"}>
            {t('lomavis.branding.mainActions.noTokensTooltip')}
          </div>
          <div className={"d-flex justify-content-end"}>
            <LomavisUpgradeSubscriptionPageLink />
          </div>
        </Tooltip>
      }

    >
      {card}
    </OverlayTrigger>
  ) : (
    card
  );
};

export default AnalyzeImageAction;
